import { Cartographic, defined, ScreenSpaceEventHandler, ScreenSpaceEventType, Math as CesiumMath, JulianDate, Color, Cartesian3, Entity, KeyboardEventModifier, ConstantProperty } from "cesium";
import { extractEntityPropertiesDeep, getUsernameAndUserIdFormat, removeEpochSuffix, removeFileExtension, reverseWaypointsEntities, showToast, trimDecimals } from "./Utility";
import Type from "./Types";
import { serviceCheckCurvature } from "../api/services/curvature";
import { toggleDirectionalArrows } from "./geojson/Arrow";
import { flattenEntityToZeroHeight, rearrangePoints } from "./EntityUtility";
import { mergeEntities } from "./globalTools/Merge";

export const openInGoogleMaps = (lat, lon, z) => {
  try {
    // Validate coordinates
    const latNum = Number(lat);
    const lonNum = Number(lon);

    if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) {
      console.warn("Invalid coordinates:", lat, lon);
      return; // Stop silently if invalid
    }

    // Safe zoom fallback
    let zoom = Number(z);
    if (!Number.isFinite(zoom) || zoom < 1 || zoom > 21) {
      zoom = 15; // Default zoom
    }

    const url = `https://www.google.com/maps?q=${latNum},${lonNum}&z=${zoom}`;
    window.open(url, "_blank"); // Open in new browser tab
  } catch (err) {
    console.error("openInGoogleMaps error:", err);
  }
};

export const setupCesiumRightClickOverlays = (viewer, { otherStates = null, editModeRef = null, resetHighlights } = {}) => {
  let spacePressed = false;

  window.addEventListener("keydown", (e) => {
    if (e.code === "Space") spacePressed = true;
  });
  window.addEventListener("keyup", (e) => {
    if (e.code === "Space") spacePressed = false;
  });

  const canvas = viewer.canvas;

  // Create a single handler for both RIGHT_CLICK and LEFT_CLICK
  const handler = new ScreenSpaceEventHandler(canvas);

  // Helper to check "edit mode" from either source
  const isEditing = () => otherStates?.editModeRef?.current === true || editModeRef?.current === true;

  // Create (or reuse) a single context menu element
  const MENU_ID = "cesium-context-menu";
  let contextMenuEl = document.getElementById(MENU_ID);
  if (!contextMenuEl) {
    contextMenuEl = document.createElement("div");
    contextMenuEl.id = MENU_ID;

    // Base styles
    contextMenuEl.style.position = "absolute";
    contextMenuEl.style.zIndex = 1000;
    contextMenuEl.style.background = "#fff";
    contextMenuEl.style.border = "1px solid #ccc";
    contextMenuEl.style.padding = "5px 0";
    contextMenuEl.style.cursor = "pointer";
    contextMenuEl.style.display = "none";
    contextMenuEl.style.margin = "10px";
    contextMenuEl.style.borderRadius = "8px";

    document.body.appendChild(contextMenuEl);
  }

  // Create (or reuse) a single info box element
  const INFO_ID = "cesium-info-box";
  let infoBoxEl = document.getElementById(INFO_ID);
  if (!infoBoxEl) {
    infoBoxEl = document.createElement("div");
    infoBoxEl.id = INFO_ID;

    // Base styles
    infoBoxEl.style.position = "absolute";
    infoBoxEl.style.background = "#f5f5f5";
    infoBoxEl.style.border = "1px solid #ccc";
    infoBoxEl.style.padding = "8px";
    infoBoxEl.style.borderRadius = "5px";
    infoBoxEl.style.zIndex = "1001"; // little above the menu if overlapped
    infoBoxEl.style.display = "none";
    infoBoxEl.style.width = "auto";

    document.body.appendChild(infoBoxEl);
  }

  // Waypoints popup
  const WAYPOINTS_ID = "cesium-waypoints-popup";
  let waypointsEl = document.getElementById(WAYPOINTS_ID);

  if (!waypointsEl) {
    waypointsEl = document.createElement("div");
    waypointsEl.id = WAYPOINTS_ID;

    // base styles
    waypointsEl.style.position = "fixed";
    waypointsEl.style.top = "50%";
    waypointsEl.style.left = "50%";
    waypointsEl.style.transform = "translate(-50%, -50%)";
    waypointsEl.style.zIndex = "1100";
    waypointsEl.style.background = "#fff";
    waypointsEl.style.border = "1px solid #ccc";
    waypointsEl.style.borderRadius = "8px";
    waypointsEl.style.padding = "10px";
    waypointsEl.style.minWidth = "280px";
    waypointsEl.style.maxWidth = "420px";
    waypointsEl.style.maxHeight = "60vh";
    waypointsEl.style.overflow = "auto";
    waypointsEl.style.display = "none";
    waypointsEl.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";

    document.body.appendChild(waypointsEl);
  }

  const hideWaypoints = () => {
    waypointsEl.style.display = "none";
  };

  // Hide helpers
  const hideContextMenu = () => {
    contextMenuEl.style.display = "none";
  };
  const hideInfoBox = () => {
    infoBoxEl.style.display = "none";
  };
  const hideAll = () => {
    hideContextMenu();
    hideInfoBox();
    hideWaypoints();
  };
  // Prevent default browser context menu (once, with cleanup)
  const onNativeContextMenu = (e) => e.preventDefault();
  window.addEventListener("contextmenu", onNativeContextMenu, { passive: false });

  // --- Context Menu (globe empty space) --------------------------------------

  /** Create a context menu item with hover + click-close */
  const createMenuItem = (label, onClick) => {
    const item = document.createElement("div");
    item.innerText = label;

    // Basic styles
    item.style.padding = "5px 15px";
    item.style.whiteSpace = "nowrap";
    item.style.borderBottom = "1px solid #ddd";

    // Hover effect
    item.addEventListener("mouseover", () => {
      item.style.background = "#eee";
    });
    item.addEventListener("mouseout", () => {
      item.style.background = "transparent";
    });

    // Click handler
    item.addEventListener("click", () => {
      try {
        onClick?.();
      } finally {
        hideContextMenu();
      }
    });

    return item;
  };

  /** Show the globe context menu at a window position with given lat/lon/height */
  const showContextMenu = (pos, llh) => {
    // Reset menu
    contextMenuEl.innerHTML = "";

    // Copy Point
    contextMenuEl.appendChild(
      createMenuItem("Copy Point", () => {
        // create text with possible adjusted height
        const text = `${llh.lat} ${llh.lon}`;

        // copy to clipboard
        navigator.clipboard.writeText(text);

        // show notification
        showToast("Coordinates copied to clipboard.", Type.NOTIFICATION.SUCCESS);
      }),
    );

    // Open in Google Maps
    contextMenuEl.appendChild(
      createMenuItem("Open in Google Maps", () => {
        openInGoogleMaps(llh.lat, llh.lon, llh.height);
      }),
    );

    // Position & show
    contextMenuEl.style.left = `${pos.x}px`;
    contextMenuEl.style.top = `${pos.y}px`;
    contextMenuEl.style.display = "block";
  };

  // --- Info Box (entity clicked) ---------------------------------------------

  /** Small button factory for the info box */
  const mkBtn = (label, onClick, { danger = false, marginBottom = "5px" } = {}) => {
    const btn = document.createElement("div");
    btn.style.fontSize = "11px";
    btn.style.fontWeight = "bold";
    btn.style.marginBottom = marginBottom;
    btn.style.color = danger ? "#fff" : "#333";
    btn.style.backgroundColor = danger ? "#e74c3c" : "#fff";
    btn.style.border = `1px solid ${danger ? "#c0392b" : "#ccc"}`;
    btn.style.padding = "4px 8px";
    btn.style.borderRadius = "6px";
    btn.style.cursor = "pointer";
    btn.style.width = "100%";
    btn.style.textAlign = "center";
    btn.style.display = "flex";
    btn.style.justifyContent = "center";
    btn.style.alignItems = "center";
    btn.textContent = label;
    btn.onclick = () => {
      try {
        onClick?.();
      } finally {
        hideInfoBox();
      }
    };
    return btn;
  };

  // --- helpers for splitting a polyline ---

  // Get 3D cartesian from screen position
  function cartesianFromPos(viewer, pos) {
    // try depth
    const c1 = viewer.scene.pickPosition(pos);
    if (defined(c1)) return c1;
    // fallback to ellipsoid
    const c2 = viewer.camera.pickEllipsoid(pos, viewer.scene.globe.ellipsoid);
    return c2;
  }

  // Compute polyline length in 3D
  function polylineLength(pts) {
    let L = 0;
    for (let i = 0; i < pts.length - 1; i++) L += Cartesian3.distance(pts[i], pts[i + 1]);
    return L;
  }

  // Project P to segment AB, return Q and t in [0,1]
  function projectToSeg(A, B, P) {
    const AB = Cartesian3.subtract(B, A, new Cartesian3());
    const AP = Cartesian3.subtract(P, A, new Cartesian3());
    const ab2 = Cartesian3.dot(AB, AB);
    if (ab2 === 0) return { Q: A, t: 0 };
    let t = Cartesian3.dot(AP, AB) / ab2;
    t = Math.max(0, Math.min(1, t));
    const Q = Cartesian3.lerp(A, B, t, new Cartesian3());
    return { Q, t };
  }

  // Find segment closest to P, return {i, Q, t}
  function findSplitOnPolyline(positions, P) {
    let best = { i: -1, Q: null, t: 0, d: Number.POSITIVE_INFINITY };
    for (let i = 0; i < positions.length - 1; i++) {
      const { Q, t } = projectToSeg(positions[i], positions[i + 1], P);
      const d = Cartesian3.distance(P, Q);
      if (d < best.d) best = { i, Q, t, d };
    }
    return best;
  }

  // Read plain properties object at time "now"
  function readPropsNow(entity, now) {
    const p = entity?.properties;
    if (!p) return {};
    if (typeof p.getValue === "function") return p.getValue(now) || {};
    return p;
  }

  // Find owning dataSource for an entity
  function findOwnerDataSource(viewer, entity) {
    let owner = null;
    viewer.dataSources?._dataSources?.forEach?.((ds) => {
      if (ds?.entities?.contains?.(entity)) owner = ds;
    });
    return owner;
  }

  // Find max numeric properties.id in a collection
  function maxPropIdInCollection(collection, now) {
    let maxId = -Infinity;
    const items = collection?.values || [];
    for (const e of items) {
      const bag = readPropsNow(e, now);
      const n = Number(bag?.id);
      if (Number.isFinite(n)) maxId = Math.max(maxId, n);
    }
    return Number.isFinite(maxId) ? maxId : 0;
  }

  // Extract static polyline style at time "now"

  function extractPolylineStyle(polyline /* no now here */) {
    return {
      width: polyline.width, // keep as Property or number
      material: polyline.material, // DO NOT call getValue here
      clampToGround: polyline.clampToGround,
      arcType: polyline.arcType,
      classificationType: polyline.classificationType,
      zIndex: polyline.zIndex,
    };
  }

  const showWaypointsPopup = (entity) => {
    const now = viewer.clock?.currentTime || JulianDate.now();
    let positions = null;

    if (entity.polyline?.positions) {
      positions = entity.polyline.positions.getValue(now);
    } else if (entity.polygon?.hierarchy) {
      const h = entity.polygon.hierarchy.getValue(now);
      positions = h?.positions || h;
    }

    if (!positions || positions.length === 0) {
      showToast("No waypoints found for this feature.", Type.NOTIFICATION.warning);
      return;
    }

    let rows = "";
    for (let i = 0; i < positions.length; i++) {
      const carto = Cartographic.fromCartesian(positions[i]);
      const lat = CesiumMath.toDegrees(carto.latitude).toFixed(7);
      const lon = CesiumMath.toDegrees(carto.longitude).toFixed(7);
      const h = carto.height.toFixed(2);
      rows += `
        <tr>
          <td style="padding:2px 6px;border:1px solid #ddd;">${i + 1}</td>
          <td style="padding:2px 6px;border:1px solid #ddd;">${lat}</td>
          <td style="padding:2px 6px;border:1px solid #ddd;">${lon}</td>
          <td style="padding:2px 6px;border:1px solid #ddd;">${h}</td>
        </tr>
      `;
    }

    waypointsEl.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <span style="font-weight:bold;font-size:13px;">Waypoints (${positions.length})</span>
        <button id="${WAYPOINTS_ID}-close" style="
          border:none;
          background:transparent;
          font-size:16px;
          cursor:pointer;
          padding:0 4px;
        ">&times;</button>
      </div>
      <div style="overflow:auto;max-height:50vh;">
        <table style="border-collapse:collapse;width:100%;font-size:11px;">
          <thead>
            <tr>
              <th style="padding:3px 6px;border:1px solid #ddd;">#</th>
              <th style="padding:3px 6px;border:1px solid #ddd;">Lat</th>
              <th style="padding:3px 6px;border:1px solid #ddd;">Lon</th>
              <th style="padding:3px 6px;border:1px solid #ddd;">Z</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;

    const closeBtn = document.getElementById(`${WAYPOINTS_ID}-close`);
    if (closeBtn) {
      closeBtn.onclick = () => hideWaypoints();
    }

    waypointsEl.style.display = "block";
  };

  /** Build and show info-box UI for a picked entity */
  const showInfoBox = (entity, pos) => {
    // Extract properties; if none, do not show
    const properties = entity?.properties;
    if (!defined(properties)) {
      hideInfoBox();
      return;
    }

    // Clear previous content
    infoBoxEl.innerHTML = "";

    // --- Title (layerName) ---
    const ownerDS = findOwnerDataSource(viewer, entity) || entity?.entityCollection?.owner;
    const layerName = ownerDS?.layerName ?? ownerDS?.name ?? "Unknown Layer";

    const titleEl = document.createElement("div");
    titleEl.textContent = "Layer Name: " + removeEpochSuffix(removeFileExtension(layerName));
    titleEl.style.gridColumn = "1 / -1";
    titleEl.style.fontWeight = "bold";
    titleEl.style.fontSize = "12px";
    titleEl.style.padding = "6px 8px";
    titleEl.style.borderBottom = "1px solid #ddd";
    titleEl.style.marginBottom = "4px";
    titleEl.style.color = "#222";
    titleEl.style.background = "#fafafa";
    titleEl.style.borderRadius = "6px";

    infoBoxEl.appendChild(titleEl);

    infoBoxEl.style.display = "grid";
    infoBoxEl.style.gridTemplateColumns = "repeat(2, minmax(0, 1fr))";
    infoBoxEl.style.columnGap = "6px";
    infoBoxEl.style.rowGap = "4px";

    if (otherStates.userCanEditingRef.current && otherStates.selectedEntitiesRef.current.length < 2) {
      infoBoxEl.appendChild(
        mkBtn("Edit Tags", () => {
          otherStates?.setEditFeatureDialog?.({ open: true, entity });
        }),
      );
    }

    if (entity.polyline && otherStates.selectedEntitiesRef.current.length < 2) {
      // Check Curvature
      infoBoxEl.appendChild(
        mkBtn("Check Curvature", () => {
          if (!entity.polyline) {
            showToast("Not a line (polyline).", Type.NOTIFICATION.WARNING);
            return;
          }

          const positions = entity.polyline.positions.getValue(JulianDate.now());
          const coords = positions.map((p) => {
            const c = Cartographic.fromCartesian(p);
            const lon = CesiumMath.toDegrees(c.longitude).toFixed(7);
            const lat = CesiumMath.toDegrees(c.latitude).toFixed(7);
            return [parseFloat(lat), parseFloat(lon)];
          });

          function addCurvaturePoint(viewer, point) {
            // Create the marker (entity)
            const entity = viewer.entities.add({
              position: Cartesian3.fromDegrees(point[1], point[0]), // lng, lat
              point: {
                pixelSize: 12,
                color: Color.RED,
                outlineColor: Color.WHITE,
                outlineWidth: 2,
              },
            });

            // Zoom to the point
            viewer.flyTo(entity);

            // Add a click handler on the globe
            const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

            handler.setInputAction(() => {
              // Remove the entity when user clicks
              viewer.entities.remove(entity);
              handler.destroy(); // Clean up event handler
            }, ScreenSpaceEventType.LEFT_CLICK);
          }

          serviceCheckCurvature({ line_coordinates: coords })
            .then((res) => {
              if (res.data.status === "success") {
                showToast("Average Curvature is: " + res.data.curvature.avg_curvature, Type.NOTIFICATION.SUCCESS);
                showToast("Max Curvature is: " + res.data.curvature.max_curvature, Type.NOTIFICATION.SUCCESS);
                showToast("Min Curvature is: " + res.data.curvature.min_curvature, Type.NOTIFICATION.SUCCESS);

                if (res.data.curvature.point_of_max_curvature) {
                  addCurvaturePoint(viewer, res.data.curvature.point_of_max_curvature);
                }
              } else {
                showToast("No Response", Type.NOTIFICATION.WARNING);
              }
            })
            .catch((err) => {
              console.error("Error", err);
              showToast(err.message, Type.NOTIFICATION.ERROR);
            });
        }),
      );
    }

    if (otherStates.userCanEditingRef.current && otherStates.selectedEntitiesRef.current.length < 2) {
      // Edit Geometry
      infoBoxEl.appendChild(
        mkBtn(
          "Edit Geometry",
          () => {
            let layerId = null;
            viewer.dataSources?._dataSources?.forEach?.((ds) => {
              if (ds.entities.contains(entity)) layerId = ds.layerId;
            });

            const mapObject = otherStates?.mapObjectsRef?.current ?? {};
            for (const key in mapObject) {
              if (mapObject[key].id === layerId) {
                const item = mapObject[key];
                otherStates?.setCurrentLayerEditing?.(item);
                otherStates?.setIsOpenAddonsMenu?.(false);
                otherStates?.setEditMode?.(true);
                break;
              }
            }
          },
          { marginBottom: "5px" },
        ),
      );
    }

    if (otherStates.userCanEditingRef.current) {
      if (entity.polyline) {
        // Reverse Waypoints
        infoBoxEl.appendChild(
          mkBtn(
            "Reverse Waypoints",
            () => {
              const viewerInstance = viewer;
              const selectedArr = otherStates.selectedEntitiesRef?.current ?? [];

              const entities = Array.isArray(selectedArr) && selectedArr.length > 0 ? selectedArr.filter(Boolean) : entity && entity.polyline ? [entity] : [];

              if (!viewerInstance || !entities.length) return;

              const entitiesSnapshot = [...entities];

              reverseWaypointsEntities(viewerInstance, otherStates.selectedEntitiesRef, otherStates.mapObjectsRef, otherStates.setMapObjects, otherStates.directionArrowSettingRef, entities);

              if (otherStates.mapObjectsRef && otherStates.setMapObjects) {
                const temp = [...(otherStates.mapObjectsRef.current ?? [])];
                entities.forEach((ent) => {
                  if (!ent) return;
                  for (const obj of temp) {
                    if (obj?.dataSource?.entities?.contains?.(ent)) {
                      obj.is_edited = true;
                      break;
                    }
                  }
                });
                otherStates.setMapObjects(temp);
              }

              showToast("The selected entity’s path has been reversed", Type.NOTIFICATION.INFO);

              if (otherStates.pushUndoAction) {
                otherStates.pushUndoAction({
                  type: "reverse_waypoints",
                  undo: () => {
                    const v = viewerInstance;
                    if (!v || !entitiesSnapshot.length) return;

                    reverseWaypointsEntities(v, otherStates.selectedEntitiesRef, otherStates.mapObjectsRef, otherStates.setMapObjects, otherStates.directionArrowSettingRef, entitiesSnapshot);

                    otherStates.setSelectedEntities?.(entitiesSnapshot);
                    if (otherStates.selectedEntitiesRef) {
                      otherStates.selectedEntitiesRef.current = entitiesSnapshot;
                    }

                    if (otherStates.mapObjectsRef && otherStates.setMapObjects) {
                      const temp2 = [...(otherStates.mapObjectsRef.current ?? [])];
                      entitiesSnapshot.forEach((ent) => {
                        if (!ent) return;
                        for (const obj of temp2) {
                          if (obj?.dataSource?.entities?.contains?.(ent)) {
                            obj.is_edited = true;
                            break;
                          }
                        }
                      });
                      otherStates.setMapObjects(temp2);
                    }
                  },
                });
              }
            },
            { marginBottom: "5px" },
          ),
        );
      }
    }

    if (otherStates.userCanEditingRef.current) {
      // Duplicate + register undo
      infoBoxEl.appendChild(
        mkBtn(
          "Duplicate",
          () => {
            const selectedArr = otherStates.selectedEntitiesRef.current ?? [];
            const created = []; // store created duplicates for undo

            if (Array.isArray(selectedArr) && selectedArr.length > 0) {
              selectedArr.forEach((ent) => {
                if (!ent) return;

                const dup = duplicateEntity(ent);
                if (dup) {
                  created.push(dup);
                }
              });
            } else if (entity) {
              const dup = duplicateEntity(entity);
              if (dup) {
                created.push(dup);
              }
            }

            if (created.length) {
              showToast("Duplicated", Type.NOTIFICATION.SUCCESS);
            }

            // register undo so global Undo button can remove duplicates
            if (created.length && otherStates.pushUndoAction) {
              otherStates.pushUndoAction({
                type: "duplicate",
                undo: () => {
                  created.forEach((ent) => {
                    if (!ent) return;
                    const coll = ent.entityCollection;

                    if (coll && coll.contains && coll.contains(ent)) {
                      coll.remove(ent);
                    } else if (coll && coll.remove) {
                      try {
                        coll.remove(ent);
                      } catch {}
                    }
                  });
                },
              });
            }
          },
          { marginBottom: "5px" },
        ),
      );
    }

    if (otherStates.userCanEditingRef.current && (otherStates.selectedEntitiesRef.current?.length ?? 0) >= 2) {
      infoBoxEl.appendChild(
        mkBtn(
          "Merge",
          async () => {
            const viewerInstance = viewer;
            const selectedArr = otherStates.selectedEntitiesRef.current ?? [];

            if (!viewerInstance || !Array.isArray(selectedArr) || selectedArr.length < 2) return;

            // build a fake selectionResult like lastSelectedWithRectangle
            const f_f = selectedArr.filter(Boolean).map((ent) => ({
              id: ent.id,
              dataSourceName: ent?.entityCollection?.owner?.layerName ?? null,
            }));

            if (!f_f.length) return;

            const selectionResult = {
              results: {
                f_f,
              },
            };

            const res = await mergeEntities(viewerInstance, selectionResult, otherStates.mapObjectsRef, otherStates.setMapObjects);
            if (!res || !res.undo) return;

            const { mutated, removed } = res.undo;

            // register undo same as RightMenuGlobalTools.handleMerge
            if (otherStates.pushUndoAction) {
              otherStates.pushUndoAction({
                type: "merge",
                undo: () => {
                  mutated.forEach((item) => {
                    const { entity, pointsBefore, propertiesBefore } = item;
                    if (!entity || !pointsBefore || !pointsBefore.length) return;

                    const flat = [];
                    pointsBefore.forEach((p) => {
                      flat.push(p.x, p.y, p.z ?? 0);
                    });

                    const cartesians = Cartesian3.fromDegreesArrayHeights(flat);

                    if (entity.polyline) {
                      entity.polyline.positions = cartesians;
                    } else {
                      entity.polyline = { positions: cartesians };
                    }

                    if (typeof propertiesBefore !== "undefined") {
                      entity.properties = propertiesBefore;
                    }
                  });

                  removed.forEach((item) => {
                    const { entity, dataSource } = item;
                    if (!entity || !dataSource || !dataSource.entities) return;
                    if (!dataSource.entities.contains(entity)) {
                      dataSource.entities.add(entity);
                    }
                  });

                  if (otherStates.mapObjectsRef && otherStates.setMapObjects) {
                    otherStates.setMapObjects([...otherStates.mapObjectsRef.current]);
                  }
                },
              });
            }

            if (otherStates.setSelectedEntities) {
              otherStates.setSelectedEntities([]);
            }
            if (otherStates.selectedEntitiesRef) {
              otherStates.selectedEntitiesRef.current = [];
            }
          },
          { marginBottom: "5px" },
        ),
      );
    }

    if (otherStates.selectedEntitiesRef.current.length < 2) {
      infoBoxEl.appendChild(
        mkBtn(
          "Show Waypoints",
          () => {
            showWaypointsPopup(entity);
          },
          { marginBottom: "5px" },
        ),
      );
    }

    if (otherStates.userCanEditingRef.current && otherStates.selectedEntitiesRef.current.length < 2) {
      infoBoxEl.appendChild(
        mkBtn(
          "Zeroing the height",
          () => {
            flattenEntityToZeroHeight(entity);
            showToast("Entity heights flattened to 0.", Type.NOTIFICATION.SUCCESS);
            flagEditedIfOwned(otherStates, entity);
          },
          { marginBottom: "5px" },
        ),
      );
    }

    if (otherStates.userCanEditingRef.current && otherStates.selectedEntitiesRef.current.length < 2) {
      if (entity.polyline) {
        // Split Line Here
        infoBoxEl.appendChild(
          mkBtn(
            "Split Line Here",
            () => {
              const now = JulianDate.now();

              // must be a polyline with at least 2 points
              if (!entity.polyline) {
                showToast("Not a line (polyline).", Type.NOTIFICATION.WARNING);
                return;
              }
              const positions = entity.polyline.positions.getValue(now);
              if (!positions || positions.length < 2) {
                showToast("Line too short.", Type.NOTIFICATION.WARNING);
                return;
              }

              // get clicked 3D point from the same screen coords "pos"
              const clicked = cartesianFromPos(viewer, pos);
              if (!defined(clicked)) {
                showToast("Cannot read click position.", Type.NOTIFICATION.ERROR);
                return;
              }

              // find best segment and split point
              const best = findSplitOnPolyline(positions, clicked);
              const EPS_T = 1e-6;
              if (best.i < 0 || best.t <= EPS_T || best.t >= 1 - EPS_T) {
                showToast("Split point is at the end.", Type.NOTIFICATION.WARNING);
                return;
              }

              // build two parts
              const left = positions.slice(0, best.i + 1);
              left.push(best.Q);
              const right = [best.Q, ...positions.slice(best.i + 1)];

              // lengths
              const L1 = polylineLength(left);
              const L2 = polylineLength(right);

              // properties cloning
              const origProps = { ...readPropsNow(entity, now) };
              const longerIsLeft = L1 >= L2;
              const propsLong = { ...origProps };
              const propsShort = { ...origProps };

              // resolve owner collection
              const ownerDS = findOwnerDataSource(viewer, entity);
              const collection = ownerDS ? ownerDS.entities : viewer.entities;

              // assign ids
              const origIdNum = Number(origProps?.id);
              if (!Number.isFinite(origIdNum)) {
                showToast("Original properties.id is not numeric.", Type.NOTIFICATION.WARNING);
              }
              const maxId = maxPropIdInCollection(collection, now);
              const newId = maxId + 2;

              if (longerIsLeft) {
                propsLong.id = Number.isFinite(origIdNum) ? origIdNum : origProps?.id;
                propsShort.id = newId;
              } else {
                propsLong.id = Number.isFinite(origIdNum) ? origIdNum : origProps?.id;
                propsShort.id = newId;
              }

              // style copy
              const style = extractPolylineStyle(entity.polyline);

              const eLongCfg = {
                name: entity.name,
                properties: propsLong,
                polyline: { positions: longerIsLeft ? left : right, ...style }, // material is OK now
              };
              const eShortCfg = {
                name: entity.name,
                properties: propsShort,
                polyline: { positions: longerIsLeft ? right : left, ...style },
              };

              // add two new entities into the same collection
              const addTo = (cfg) => collection.add(cfg);

              const eLong = addTo(eLongCfg);
              const eShort = addTo(eShortCfg);

              // remove original and its vertex markers
              try {
                removeVertexMarkersFor(viewer, entity);
              } catch {}
              try {
                if (ownerDS) ownerDS.entities.remove(entity);
                else viewer.entities.remove(entity);
              } catch {}

              // flag edited for layer bookkeeping
              try {
                flagEditedIfOwned(otherStates, eLong);
              } catch {}

              // optional: update arrows if your layer tracks it
              try {
                if (ownerDS) {
                  for (const key in otherStates?.mapObjectsRef?.current) {
                    const obj = otherStates?.mapObjectsRef?.current[key];
                    if (obj?.dataSource === ownerDS) {
                      const s = obj.arrow_visibility ? true : false;
                      toggleDirectionalArrows(viewer, ownerDS, s, otherStates?.directionArrowSettingRef);
                      break;
                    }
                  }
                }
              } catch {}

              // notify
              showToast("Line split into two entities.", Type.NOTIFICATION.SUCCESS);
            },
            { marginBottom: "5px" },
          ),
        );
      }
    }

    if (otherStates.userCanEditingRef.current && otherStates.selectedEntitiesRef.current.length < 2) {
      infoBoxEl.appendChild(
        mkBtn(
          "Copy To Another Layer",
          () => {
            otherStates.setMoveToAnotherLayerDialog({
              open: true,
              data: [entity],
            });
          },
          { marginBottom: "5px" },
        ),
      );
    }

    if (otherStates.userCanEditingRef.current) {
      infoBoxEl.appendChild(
        mkBtn(
          "Rearrange Points",
          () => {
            const selectedArr = otherStates.selectedEntitiesRef.current ?? [];

            const entities = Array.isArray(selectedArr) && selectedArr.length > 0 ? selectedArr : entity ? [entity] : [];

            if (!entities.length) return;

            const result = rearrangePoints(entities, otherStates.simplifyToleranceSettingRef.current?.rearrange, otherStates.setMapObjects);

            const undoItems = result?.undo || [];

            if (undoItems.length && otherStates.pushUndoAction) {
              otherStates.pushUndoAction({
                type: "rearrange_points",
                undo: () => {
                  undoItems.forEach((item) => {
                    const { entity, oldPositions } = item;
                    if (!entity || !oldPositions || !oldPositions.length) return;

                    const restored = oldPositions.map((p) => Cartesian3.clone(p));
                    if (entity.polyline) {
                      entity.polyline.positions = new ConstantProperty(restored);
                    } else {
                      entity.polyline = { positions: restored };
                    }
                  });

                  if (otherStates.mapObjectsRef && otherStates.setMapObjects) {
                    otherStates.setMapObjects([...otherStates.mapObjectsRef.current]);
                  }

                  const ents = undoItems.map((u) => u.entity).filter(Boolean);
                  if (otherStates.setSelectedEntities) {
                    otherStates.setSelectedEntities(ents);
                  }
                  if (otherStates.selectedEntitiesRef) {
                    otherStates.selectedEntitiesRef.current = ents;
                  }
                },
              });
            }

            showToast("Rearranged points", Type.NOTIFICATION.SUCCESS);
          },
          { marginBottom: "5px" },
        ),
      );
    }

    function readProps(viewer, e) {
      const p = e?.properties;
      if (!p) return null;
      if (typeof p.getValue === "function") return p.getValue(viewer.clock?.currentTime);
      return p;
    }
    // remove vertex markers that belong to a specific entity.id
    function removeVertexMarkersFor(viewer, targetEntity) {
      if (!viewer || !targetEntity) return;

      const ownerId = targetEntity.id;
      const coll = viewer.entities;

      if (!coll || !coll.values) return;

      // iterate backwards, no slice()
      for (let i = coll.values.length - 1; i >= 0; i--) {
        const e = coll.values[i];
        const bag = readProps(viewer, e);
        if (bag && bag._isHighlightVertexMarker === true && bag._ownerEntityId === ownerId) {
          try {
            coll.remove(e);
          } catch {}
        }
      }
    }

    // flag edited on mapObjects list if entity belongs to a datasource object
    function flagEditedIfOwned(otherStates, entity) {
      const mapObjects = otherStates?.mapObjectsRef?.current ?? [];
      const obj = mapObjects.find((obj) => obj?.dataSource?.entities?.contains?.(entity));
      if (obj) {
        obj.is_edited = true;
        otherStates?.setMapObjects?.([...mapObjects]);
      }
    }

    if (otherStates.userCanEditingRef.current) {
      infoBoxEl.appendChild(
        mkBtn(
          "Remove Feature(s)",
          () => {
            const selectedArr = otherStates.selectedEntitiesRef.current ?? [];
            const removed = [];

            const affectedDataSources = new Set();

            // Build a fast lookup for layerName -> mapObject (so we don't loop mapObjects per delete)
            const layerToMapObject = new Map();
            const mapObjects = otherStates?.mapObjectsRef?.current;
            if (mapObjects) {
              for (const key in mapObjects) {
                const mo = mapObjects[key];
                const ln = mo?.dataSource?.layerName;
                if (ln && !layerToMapObject.has(ln)) layerToMapObject.set(ln, mo);
              }
            }

            const getOwnerDataSource = (coll) => {
              // For ds.entities, coll.owner is usually the DataSource (e.g., CustomDataSource)
              const owner = coll?.owner;
              if (!owner) return null;
              if (owner?.entities === coll) return owner; // DataSource-like
              return null;
            };

            const removeOneEntity = (ent) => {
              if (!ent) return;

              const coll = ent.entityCollection;
              if (!coll) return;

              // Immediate visual removal (defer real remove)
              try {
                ent.show = false;
              } catch {}

              removed.push({ entity: ent, collection: coll });

              if (coll !== viewer.entities) {
                const ds = getOwnerDataSource(coll);
                if (ds) {
                  try {
                    flagEditedIfOwned(otherStates, ent);
                  } catch {}
                  affectedDataSources.add(ds);
                }
              }

              // Remove vertex markers now (cheap) and defer heavy Cesium sync from coll.remove
              removeVertexMarkersFor(viewer, ent); // remove white points

              setTimeout(() => {
                try {
                  coll.remove(ent);
                } catch {}
              }, 0);
            };

            if (Array.isArray(selectedArr) && selectedArr.length > 0) {
              selectedArr.forEach((ent) => removeOneEntity(ent));
            } else {
              removeOneEntity(entity);
            }

            // Update directional arrows only for affected DS (no scanning all DS)
            if (affectedDataSources.size > 0) {
              affectedDataSources.forEach((ds) => {
                try {
                  const mo = layerToMapObject.get(ds?.layerName);
                  const s = mo?.arrow_visibility ? true : false;
                  toggleDirectionalArrows(viewer, ds, s, otherStates?.directionArrowSettingRef);
                } catch {}
              });
            }

            // Undo support
            if (removed.length && otherStates.pushUndoAction) {
              otherStates.pushUndoAction({
                type: "delete_features",
                undo: () => {
                  removed.forEach(({ entity, collection }) => {
                    if (!entity || !collection) return;
                    try {
                      // If entity still exists (was only hidden) restore it
                      entity.show = true;

                      // If it was removed by deferred remove, re-add it
                      if (!collection.contains(entity)) {
                        collection.add(entity);
                      }
                    } catch {}
                  });
                },
              });
            }
          },
          { danger: true, marginBottom: "5px" },
        ),
      );
    }

    // Properties table
    const table = document.createElement("table");
    table.style.gridColumn = "1 / -1";
    table.style.borderCollapse = "collapse";
    table.style.width = "100%";
    table.style.fontSize = "12px";
    table.style.backgroundColor = "white";

    const featureProperties = extractEntityPropertiesDeep(entity);
    for (const fKey in featureProperties) {
      const { key, value } = featureProperties[fKey];

      const row = document.createElement("tr");

      const keyCell = document.createElement("td");
      keyCell.style.border = "1px solid #ddd";
      keyCell.style.padding = "4px";
      keyCell.style.fontWeight = "bold";
      keyCell.style.color = "#333";
      keyCell.textContent = key;

      const valueContent = document.createElement("span");
      valueContent.textContent = value; // set text
      valueContent.style.display = "-webkit-box"; // enable line clamping
      valueContent.style.maxWidth = "100px"; // cap width
      valueContent.style.whiteSpace = "normal"; // allow wrapping
      valueContent.style.wordBreak = "break-word"; // break long words
      valueContent.style.overflowWrap = "anywhere"; // wrap anywhere
      valueContent.style.overflow = "hidden"; // hide overflow
      valueContent.style.webkitBoxOrient = "vertical"; // required for clamp
      valueContent.style.webkitLineClamp = "5"; // show at most 5 lines
      valueContent.title = value;

      const valueCell = document.createElement("td");
      valueCell.style.border = "1px solid #ddd";
      valueCell.style.padding = "4px";
      valueCell.style.color = "#555";
      valueCell.textContent = "";
      valueCell.appendChild(valueContent);

      row.appendChild(keyCell);
      row.appendChild(valueCell);
      table.appendChild(row);
    }

    infoBoxEl.appendChild(table);

    // Position & show
    infoBoxEl.style.display = "grid";
    infoBoxEl.style.left = `${pos.x + 10}px`;
    infoBoxEl.style.top = `${pos.y + 10}px`;

    // --- keep inside viewport
    requestAnimationFrame(() => {
      const boxRect = infoBoxEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newLeft = pos.x + 10;
      let newTop = pos.y + 10;

      if (boxRect.right > viewportWidth) {
        newLeft = viewportWidth - boxRect.width - 10;
      }

      if (boxRect.bottom > viewportHeight) {
        newTop = viewportHeight - boxRect.height - 10;
      }

      infoBoxEl.style.left = `${Math.max(10, newLeft)}px`;
      infoBoxEl.style.top = `${Math.max(10, newTop)}px`;
    });
  };

  // --- ScreenSpaceEvent Handlers --------------------------------------------

  // RIGHT_CLICK => Decide which overlay to show
  handler.setInputAction((evt) => {
    if (isEditing()) {
      hideAll();
      return;
    }

    resetHighlights?.();
    viewer.scene.requestRender();

    const pos = { x: evt.position.x, y: evt.position.y };

    // single-use postRender listener
    const once = () => {
      try {
        const picked = viewer.scene.pick(pos);
        if (defined(picked) && picked.id) {
          hideAll();
          showInfoBox(picked.id, pos);
          return;
        }
        if (defined(picked)) {
          hideAll();
          return;
        }
        const cartesian = viewer.scene.pickPosition(pos);
        if (!defined(cartesian)) {
          hideAll();
          return;
        }

        const carto = Cartographic.fromCartesian(cartesian);
        const lon = trimDecimals(CesiumMath.toDegrees(carto.longitude), 7);
        const lat = trimDecimals(CesiumMath.toDegrees(carto.latitude), 7);
        const height = carto.height.toFixed(2);
        hideAll();
        showContextMenu(pos, { lat, lon, height });
      } finally {
        viewer.scene.postRender.removeEventListener(once);
      }
    };

    viewer.scene.postRender.addEventListener(once);
  }, ScreenSpaceEventType.RIGHT_CLICK);

  // LEFT_CLICK => Hide overlays if empty space is clicked (keeps your semantics)
  handler.setInputAction((evt) => {
    const picked = viewer.scene.pick(evt.position);
    if (!defined(picked) || !picked.id) {
      hideAll();
    }
  }, ScreenSpaceEventType.LEFT_CLICK);

  // Also hide on any normal click on the canvas (useful when clicking elsewhere)
  const onCanvasClick = () => hideAll();
  canvas.addEventListener("click", onCanvasClick);

  // CTRL + LEFT_CLICK => custom action on entity
  handler.setInputAction(
    (evt) => {
      if (!otherStates?.userCanEditingRef.current) return;

      resetHighlights?.();
      viewer.scene.requestRender();

      // keep click position
      const pos = { x: evt.position.x, y: evt.position.y };

      // single-use postRender listener
      const once = () => {
        try {
          // hideAll?.(); // uncomment if you have this

          const picked = viewer.scene.pick(pos);
          if (!defined(picked) || !picked.id) return;

          const entity = picked.id;

          let layerId = null;
          viewer.dataSources?._dataSources?.forEach?.((ds) => {
            // check which datasource owns this entity
            if (ds.entities.contains(entity)) layerId = ds.layerId;
          });

          if (!layerId) return;

          const mapObject = otherStates?.mapObjectsRef?.current ?? {};
          for (const key in mapObject) {
            const item = mapObject[key];
            if (item.id === layerId) {
              // set editing state for this layer
              otherStates?.setCurrentLayerEditing?.(item);
              otherStates?.setIsOpenAddonsMenu?.(false);
              otherStates?.setEditMode?.(true);

              break;
            }
          }
        } finally {
          // remove listener after first run
          viewer.scene.postRender.removeEventListener(once);
        }
      };

      // run logic after next render
      viewer.scene.postRender.addEventListener(once);
      viewer.scene.requestRender();
    },
    ScreenSpaceEventType.LEFT_CLICK,
    KeyboardEventModifier.CTRL,
  );

  handler.setInputAction(
    (evt) => {
      if (!otherStates?.userCanEditingRef.current) return;
      const picked = viewer.scene.pick(evt.position);

      if (defined(picked) && picked.id) {
        const entity = picked.id;

        otherStates?.setEditFeatureDialog?.({ open: true, entity });
      }
    },
    ScreenSpaceEventType.RIGHT_CLICK,
    KeyboardEventModifier.SHIFT,
  );

  // LEFT_CLICK + Space => trigger Split Line Here
  handler.setInputAction((evt) => {
    if (!otherStates?.userCanEditingRef.current) return;
    if (!spacePressed) return;

    if (editModeRef.current) return;

    const picked = viewer.scene.pick(evt.position);
    if (!defined(picked) || !picked.id || !picked.id.polyline) return;

    const entity = picked.id;
    const pos = { x: evt.position.x, y: evt.position.y };

    const now = JulianDate.now();
    const positions = entity.polyline.positions.getValue(now);
    if (!positions || positions.length < 2) return;

    const clicked = viewer.scene.pickPosition(pos);
    if (!defined(clicked)) return;

    const best = findSplitOnPolyline(positions, clicked);
    const EPS_T = 1e-6;
    if (best.i < 0 || best.t <= EPS_T || best.t >= 1 - EPS_T) return;

    const left = positions.slice(0, best.i + 1);
    left.push(best.Q);
    const right = [best.Q, ...positions.slice(best.i + 1)];

    const L1 = polylineLength(left);
    const L2 = polylineLength(right);
    const origProps = { ...readPropsNow(entity, now) };
    const longerIsLeft = L1 >= L2;
    const propsLong = { ...origProps };
    const propsShort = { ...origProps };

    const ownerDS = findOwnerDataSource(viewer, entity);
    const collection = ownerDS ? ownerDS.entities : viewer.entities;
    const maxId = maxPropIdInCollection(collection, now);
    const newId = maxId + 2;

    propsShort.id = newId;

    const style = extractPolylineStyle(entity.polyline);

    const eLongCfg = {
      name: entity.name,
      properties: propsLong,
      polyline: { positions: longerIsLeft ? left : right, ...style },
    };
    const eShortCfg = {
      name: entity.name,
      properties: propsShort,
      polyline: { positions: longerIsLeft ? right : left, ...style },
    };

    const addTo = (cfg) => collection.add(cfg);
    const eLong = addTo(eLongCfg);
    const eShort = addTo(eShortCfg);

    try {
      if (ownerDS) ownerDS.entities.remove(entity);
      else viewer.entities.remove(entity);
    } catch {}

    showToast("Line split into two entities.", Type.NOTIFICATION.SUCCESS);
  }, ScreenSpaceEventType.LEFT_CLICK);

  const onKeyDown = (e) => {
    if (e.code === "Space") spacePressed = true;
  };
  const onKeyUp = (e) => {
    if (e.code === "Space") spacePressed = false;
  };

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);

  // --- Cleanup ---------------------------------------------------------------

  /**
   * Cleanup function; call this in useEffect return () => cleanup()
   * - Destroys the Cesium ScreenSpaceEventHandler
   * - Removes native event listeners
   * - (Keeps DOM nodes for reuse across mounts; remove if you prefer)
   */
  const cleanup = () => {
    try {
      handler?.destroy?.();
    } catch {}

    window.removeEventListener("contextmenu", onNativeContextMenu);
    canvas.removeEventListener("click", onCanvasClick);
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
  };

  return cleanup;
};

export function duplicateEntity(entity, options = {}) {
  // --- Guards ---
  if (!entity || typeof entity !== "object") {
    showToast("duplicateEntity: entity must be a Cesium Entity.", Type.NOTIFICATION.ERROR);
    return null;
  }

  // Resolve the GeoJsonDataSource from the entity itself
  const dataSource = entity?.entityCollection?.owner;
  if (!dataSource || !dataSource.entities) {
    showToast("duplicateEntity: Could not resolve dataSource from entity.entityCollection.owner.", Type.NOTIFICATION.ERROR);
    return null;
  }

  const entities = dataSource.entities; // This is a Cesium EntityCollection
  const { idFactory, idPrefix, userData } = options;

  // --- Helpers ---

  /** Create a unique id not present in this EntityCollection. */
  const genUniqueId = () => {
    const base = typeof idFactory === "function" ? String(idFactory()) : `${idPrefix ?? (entity.id ? String(entity.id) : "entity")}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

    if (!entities.getById(base)) return base;
    let i = 1;
    while (entities.getById(`${base}_${i}`)) i++;
    return `${base}_${i}`;
  };

  /** Snapshot plain values from Cesium Property or return value directly. */
  const snap = (maybeProp) => {
    if (maybeProp && typeof maybeProp.getValue === "function") {
      try {
        const t = typeof JulianDate !== "undefined" && JulianDate.now ? JulianDate.now() : undefined;
        return maybeProp.getValue(t);
      } catch {
        return maybeProp;
      }
    }
    return maybeProp;
  };

  /** Find the max id inside properties.id among all entities. */
  const findMaxPropertiesId = () => {
    let maxId = -Infinity;
    entities.values.forEach((e) => {
      try {
        const props = e.properties?.getValue ? e.properties.getValue() : e.properties;
        const pid = props?.id;
        if (typeof pid === "number" && Number.isFinite(pid)) {
          if (pid > maxId) maxId = pid;
        }
      } catch {
        // ignore
      }
    });
    return Number.isFinite(maxId) ? maxId : 0;
  };

  /** Build a minimal initializer object if clone is unavailable. */
  const buildInit = (e, newId) => {
    const init = { id: newId };

    if (e.position) init.position = e.position;
    if (e.orientation) init.orientation = e.orientation;

    if (e.billboard) init.billboard = e.billboard;
    if (e.point) init.point = e.point;
    if (e.label) init.label = e.label;
    if (e.polyline) init.polyline = e.polyline;
    if (e.polygon) init.polygon = e.polygon;
    if (e.ellipse) init.ellipse = e.ellipse;
    if (e.corridor) init.corridor = e.corridor;
    if (e.rectangle) init.rectangle = e.rectangle;

    // Copy properties and then adjust .id
    let props = {};
    try {
      if (e.properties?.getValue) {
        props = { ...e.properties.getValue() };
      } else if (e.properties) {
        props = { ...e.properties };
      }
    } catch {
      props = {};
    }

    // Set new properties.id = max(properties.id) + 2
    const maxPid = findMaxPropertiesId();
    props.id = maxPid + 2;

    const creator = getUsernameAndUserIdFormat(userData);
    if (creator) {
      props.creator = creator;
    }

    init.properties = props;

    if (e.name) init.name = e.name;
    if (e.description) init.description = snap(e.description);

    return init;
  };

  // --- Main ---

  const newId = genUniqueId();

  // Use Cesium's clone if available
  if (typeof entity.clone === "function") {
    try {
      const cloned = entity.clone();
      cloned.id = newId;

      // Update properties.id on the clone
      let props = {};
      try {
        props = cloned.properties?.getValue ? { ...cloned.properties.getValue() } : { ...cloned.properties };
      } catch {
        props = {};
      }
      const maxPid = findMaxPropertiesId();
      props.id = maxPid + 2;

      const creator = getUsernameAndUserIdFormat(userData);
      if (creator) {
        props.creator = creator;
      }

      cloned.properties = props;

      return entities.add(cloned);
    } catch (err) {
      console.error("Entity.clone failed:", err);
      showToast("duplicateEntity: Failed to clone entity.", Type.NOTIFICATION.ERROR);
      return null;
    }
  }

  // Fallback: build initializer
  try {
    const init = buildInit(entity, newId);
    return entities.add(init);
  } catch (err) {
    console.error("Entity init failed:", err);
    showToast("duplicateEntity: Failed to build/init new entity.", Type.NOTIFICATION.ERROR);
    return null;
  }
}

export function addEntityWithOnlyPositions(sourceEntityOrArray, dataSource, propreties) {
  // normalize input to array
  const sources = Array.isArray(sourceEntityOrArray) ? sourceEntityOrArray : [sourceEntityOrArray];
  if (!sources || sources.length === 0 || !dataSource) return Array.isArray(sourceEntityOrArray) ? [] : null;

  const now = new Date();
  const created = [];

  // helper to build properties object from various inputs
  function buildPropertiesObj(source, propInput) {
    let propertiesObj = {};

    if (propInput) {
      // If propInput is an array of key/value entries
      if (Array.isArray(propInput) && propInput.length > 0 && propInput.every((it) => it && typeof it.key !== "undefined")) {
        for (const item of propInput) {
          if (item && typeof item.key !== "undefined") propertiesObj[item.key] = item.value;
        }
        return propertiesObj;
      }

      // If propInput is a plain object (per-entity or common)
      if (!Array.isArray(propInput) && typeof propInput === "object") {
        return { ...propInput };
      }
    }

    // fallback: copy from source.properties if available
    const p = source && source.properties;
    if (p) {
      try {
        if (typeof p.getValue === "function") {
          const val = p.getValue(now);
          if (val && typeof val === "object") return { ...val };
        }
      } catch (e) {
        // ignore
      }
      // enumerable fallback
      for (const k in p) {
        if (Object.prototype.hasOwnProperty.call(p, k)) {
          const v = p[k];
          if (typeof v !== "function") propertiesObj[k] = v;
        }
      }
    }

    return propertiesObj;
  }

  // iterate sources and create corresponding entities
  for (let i = 0; i < sources.length; i++) {
    const sourceEntity = sources[i];
    if (!sourceEntity) {
      created.push(null);
      continue;
    }

    let newEntity = null;

    if (sourceEntity.polyline) {
      // get positions and material, unwrapping Property if needed
      const pos = sourceEntity.polyline.positions?.getValue ? sourceEntity.polyline.positions.getValue(now) : sourceEntity.polyline.positions;
      const material = sourceEntity.polyline.material?.getValue ? sourceEntity.polyline.material.getValue(now) : sourceEntity.polyline.material;
      newEntity = new Entity({
        polyline: {
          positions: pos,
          width: sourceEntity.polyline.width?.getValue ? sourceEntity.polyline.width.getValue(now) : sourceEntity.polyline.width || 2,
          material: material instanceof Color ? material : typeof Color !== "undefined" ? Color.RED : material,
        },
      });
    } else if (sourceEntity.polygon) {
      // get hierarchy and material, unwrapping Property if needed
      const pos = sourceEntity.polygon.hierarchy?.getValue ? sourceEntity.polygon.hierarchy.getValue(now) : sourceEntity.polygon.hierarchy;
      const material = sourceEntity.polygon.material?.getValue ? sourceEntity.polygon.material.getValue(now) : sourceEntity.polygon.material;
      let polyMaterial = material instanceof Color ? material : typeof Color !== "undefined" ? (Color.BLUE && Color.BLUE.withAlpha ? Color.BLUE.withAlpha(0.4) : Color.BLUE) : material;
      newEntity = new Entity({
        polygon: {
          hierarchy: pos,
          material: polyMaterial,
        },
      });
    }

    if (!newEntity) {
      created.push(null);
      continue;
    }

    // determine which properties to use for this specific source
    let propForThis;
    if (Array.isArray(propreties) && propreties.length === sources.length) {
      // treat propreties as per-entity list
      propForThis = propreties[i];
    } else {
      // use the shared propreties argument (could be array-of-keyvals or object)
      propForThis = propreties;
    }

    const propertiesObj = buildPropertiesObj(sourceEntity, propForThis);

    if (Object.keys(propertiesObj).length > 0) {
      try {
        newEntity.properties = propertiesObj;
      } catch (e) {
        newEntity._properties = propertiesObj;
      }
    }

    dataSource.entities.add(newEntity);
    created.push(newEntity);
  }

  // return same shape as input
  return Array.isArray(sourceEntityOrArray) ? created : created[0] || null;
}
